const PUBLIC_SIGN_ENTRY_PATH = '/app/sign';
const PUBLIC_SIGN_LEGACY_PATH_PATTERN = /^\/app\/sign\/([0-9a-f-]+)\/?$/i;
const PUBLIC_SIGN_TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizePublicSignToken = (value) => {
  if (typeof value !== 'string' || !PUBLIC_SIGN_TOKEN_PATTERN.test(value)) return null;
  return value.toLowerCase();
};

export const capturePublicSignCapability = (locationLike) => {
  const pathname = String(locationLike?.pathname || '');
  const hash = String(locationLike?.hash || '');
  const legacyMatch = PUBLIC_SIGN_LEGACY_PATH_PATTERN.exec(pathname);
  const isFragmentEntry = pathname === PUBLIC_SIGN_ENTRY_PATH || pathname === `${PUBLIC_SIGN_ENTRY_PATH}/`;

  if (!legacyMatch && !isFragmentEntry) {
    return { isSignEntry: false, token: null, safePath: null };
  }

  // A fragment is intentionally accepted only as the complete canonical UUID.
  // It is never decoded, copied to a query string, or placed in history state.
  const fragmentValue = hash.startsWith('#') ? hash.slice(1) : '';
  const token = fragmentValue
    ? normalizePublicSignToken(fragmentValue)
    : normalizePublicSignToken(legacyMatch?.[1]);

  return {
    isSignEntry: true,
    token,
    safePath: PUBLIC_SIGN_ENTRY_PATH,
  };
};

const installNoReferrerPolicy = (documentLike) => {
  if (!documentLike?.head || typeof documentLike.createElement !== 'function') return;
  let policy = documentLike.querySelector?.('meta[name="referrer"]');
  if (!policy) {
    policy = documentLike.createElement('meta');
    policy.name = 'referrer';
    documentLike.head.appendChild(policy);
  }
  policy.content = 'no-referrer';
};

export const createPublicSignCapabilityBootstrap = () => {
  let retainedToken = null;
  let signEntryWasCaptured = false;

  return (browserWindow) => {
    if (signEntryWasCaptured) return retainedToken;
    if (!browserWindow?.location || !browserWindow?.history) return null;

    const captured = capturePublicSignCapability(browserWindow.location);
    if (!captured.isSignEntry) return null;

    // Scrub both the new fragment and the legacy path token before React mounts.
    // Do not preserve search parameters: a signing capability must not be moved
    // into a query string or leak through a later navigation referrer.
    browserWindow.history.replaceState(
      null,
      '',
      captured.safePath,
    );
    installNoReferrerPolicy(browserWindow.document);
    retainedToken = captured.token;
    signEntryWasCaptured = true;
    return retainedToken;
  };
};

const bootstrap = createPublicSignCapabilityBootstrap();

export const bootstrapPublicSignCapability = (browserWindow = window) => bootstrap(browserWindow);
